import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { History, RefreshCw, Loader2, Download } from "lucide-react";
import { Button } from "./ui/button";
import { fetchVersions, replaceDocumentFile, versionDownloadUrl } from "../lib/api";

export const VersionHistory = ({ doc, readOnly, onReplaced }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["versions", doc.id], queryFn: () => fetchVersions(doc.id) });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const replace = async (f) => {
    setBusy(true);
    try { const r = await replaceDocumentFile(doc.id, f); qc.invalidateQueries({ queryKey: ["versions", doc.id] }); onReplaced?.(); toast.success(`File replaced — ${r.versions} earlier version${r.versions === 1 ? "" : "s"} kept`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not replace"); } finally { setBusy(false); }
  };
  const versions = data?.versions || [];
  return (
    <div className="mt-5 rounded-xl border border-dashed border-border p-3 text-xs" data-testid="version-history">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-muted-foreground"><History className="h-3.5 w-3.5" /> {versions.length ? `${versions.length} earlier version${versions.length === 1 ? "" : "s"}` : "No earlier versions"}</span>
        {!readOnly && doc.storage_path && <>
          <input ref={fileRef} type="file" className="hidden" data-testid="replace-file-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) replace(f); e.target.value = ""; }} />
          <Button size="sm" variant="outline" className="h-7 gap-1 rounded-full" data-testid="replace-file-btn" disabled={busy} onClick={() => fileRef.current?.click()}>{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Replace file</Button>
        </>}
      </div>
      {versions.length > 0 && (
        <ul className="mt-2 space-y-1">{versions.map((v, i) => (
          <li key={v.storage_path} className="flex items-center justify-between gap-2" data-testid={`version-row-${i}`}>
            <span className="truncate text-muted-foreground">v{versions.length - i} · {v.original_filename || "file"} · replaced {new Date(v.replaced_at).toLocaleDateString()}{v.by ? ` by ${v.by}` : ""}</span>
            <a href={versionDownloadUrl(doc.id, versions.length - 1 - i)} className="inline-flex items-center gap-1 text-primary hover:underline shrink-0" data-testid={`version-download-${i}`}><Download className="h-3 w-3" /> Download</a>
          </li>))}</ul>
      )}
    </div>
  );
};
